import { useEffect, useState } from "react";
import { Sparkles, Loader2, SendHorizontal } from "lucide-react";
import { adminAnalyticsAsk, adminAnalyticsSuggestions } from "@/lib/api";

/** Natural-language analytics: ask a question, get a plain-English answer grounded in real numbers. */
export default function AskAnalytics({ rng = 30 }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => { adminAnalyticsSuggestions().then((d) => setSuggestions(d.suggestions || [])).catch(() => {}); }, []);

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text || busy) return;
    setQ(text); setBusy(true); setAnswer("");
    try { const d = await adminAnalyticsAsk(text, rng); setAnswer(d.answer); }
    catch (e) { setAnswer(e?.response?.data?.detail || "The assistant is unavailable right now — please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-slate-900 text-white p-5 mb-6" data-testid="ask-analytics">
      <div className="flex items-center gap-2 mb-3">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand"><Sparkles size={16} /></span>
        <div>
          <div className="font-heading font-bold leading-tight">Ask your analytics</div>
          <div className="text-[11px] text-slate-400">Plain-English answers from your real numbers (last {rng} days)</div>
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="e.g. How did my website perform this month?" data-testid="ask-analytics-input"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-500" />
        <button onClick={() => ask()} disabled={busy} data-testid="ask-analytics-send"
          className="grid h-8 w-8 place-items-center rounded-lg bg-brand disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <SendHorizontal size={15} />}
        </button>
      </div>
      {!answer && suggestions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {suggestions.map((s, i) => (
            <button key={s} onClick={() => ask(s)} data-testid={`ask-analytics-suggestion-${i}`}
              className="text-[12px] rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 transition-colors">{s}</button>
          ))}
        </div>
      )}
      {(busy || answer) && (
        <div className="mt-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-slate-100 leading-relaxed" data-testid="ask-analytics-answer">
          {busy ? <span className="text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Reading your numbers…</span> : answer}
        </div>
      )}
    </div>
  );
}
